import { styles } from "../styles";
import { parse, isValid, addDays } from "date-fns";
import {
  isSeasonFlexAllowed,
  seasonFlexDisabledReason,
  utcTodayAsLocalDate,
} from "../helpers";
import { OptionCard } from "../common/OptionCard";
import { DatePicker } from "../common/DatePicker";
import { CalendarDays, CalendarRange } from "lucide-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { LeagueFormValues } from "../leagueFormSchema";
import { Clubhouse_LeagueWizardScheduleMode } from "../../enums";

export function LeagueDatesStep() {
  const {
    control,
    formState: { errors },
  } = useFormContext<LeagueFormValues>();

  // Start can't be before today; end can't be before the chosen start. Uses the
  // UTC day (not local startOfToday) to match the API's BeInFutureDate rule —
  // otherwise local "today" is selectable but rejected once past UTC midnight.
  const minStart = utcTodayAsLocalDate();
  const startValue = useWatch({ control, name: "leagueStartDate" });
  const parsedStart = startValue
    ? parse(startValue, "yyyy-MM-dd", new Date())
    : undefined;
  // End must be at least one day after start (backend rejects same-day), so the
  // end picker's floor is the day after the chosen start.
  const endMinDate =
    parsedStart && isValid(parsedStart) ? addDays(parsedStart, 1) : minStart;

  const leagueScoringType = useWatch({
    control,
    name: "wizard.leagueScoringType",
  });
  const flexDisabled = !isSeasonFlexAllowed(leagueScoringType);
  const flexDisabledMessage = seasonFlexDisabledReason(leagueScoringType);
  // A scoring format that rules Flex out also corrects an already-chosen Flex,
  // but that lives in the wizard: the operator can change the scoring format
  // and then jump straight to Review, never rendering this step again.

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="leagueStartDate"
            className="mulish text-sm font-semibold text-text-primary"
          >
            Start Date
          </label>
          <Controller
            name="leagueStartDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="leagueStartDate"
                value={field.value}
                onChange={field.onChange}
                minDate={minStart}
                placeholder="Select start date"
                aria-invalid={!!errors.leagueStartDate}
              />
            )}
          />
          {errors.leagueStartDate && (
            <p className={styles.wizardErrorCls}>
              {errors.leagueStartDate.message}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="leagueEndDate"
            className="mulish text-sm font-semibold text-text-primary"
          >
            End Date
          </label>
          <Controller
            name="leagueEndDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="leagueEndDate"
                value={field.value}
                onChange={field.onChange}
                minDate={endMinDate}
                placeholder="Select end date"
                aria-invalid={!!errors.leagueEndDate}
              />
            )}
          />
          {errors.leagueEndDate && (
            <p className={styles.wizardErrorCls}>
              {errors.leagueEndDate.message}
            </p>
          )}
        </div>
      </div>

      <Controller
        name="wizard.scheduleMode"
        control={control}
        render={({ field }) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <OptionCard
              align="left"
              icon={<CalendarDays className="size-7" />}
              title="Fixed"
              description="Everyone plays each round during the same scheduled window."
              selected={
                field.value === Clubhouse_LeagueWizardScheduleMode.WeeklyRounds
              }
              onClick={() =>
                field.onChange(Clubhouse_LeagueWizardScheduleMode.WeeklyRounds)
              }
            />
            <OptionCard
              align="left"
              icon={<CalendarRange className="size-7" />}
              title="Flex"
              description="Players can play every round any time during the season."
              selected={
                field.value === Clubhouse_LeagueWizardScheduleMode.SeasonFlex
              }
              disabled={flexDisabled}
              onClick={() =>
                field.onChange(Clubhouse_LeagueWizardScheduleMode.SeasonFlex)
              }
            />
          </div>
        )}
      />
      {flexDisabledMessage && (
        <p className="mulish text-sm font-semibold text-text-primary">
          {flexDisabledMessage}
        </p>
      )}
      {errors.wizard?.scheduleMode && (
        <p className={styles.wizardErrorCls}>
          {errors.wizard.scheduleMode.message}
        </p>
      )}
    </div>
  );
}
